import { useMemo } from 'react';
import { alpha, getLuminance, useTheme } from '@mui/material/styles';

/* ----------------------------------------------------------------------------
 * Metric colours: one hue per metric, channel = shade (dark = Store, light = Online).
 * DASHBOARD_COLORS is the original (dark theme) palette and is kept unchanged.
 * ---------------------------------------------------------------------------*/

export const DASHBOARD_COLORS = {
  orders: {
    store: '#497eab',
    online: '#90CAF9',
  },
  orderValue: {
    store: '#2E7D32',
    online: '#A5D6A7',
  },
  salesMargins: {
    store: '#17b389',
    online: '#85ccb9',
  },
  returns: '#E15759',
  toShip: '#F6C1C7',
  toShipBackground: '#FDEDEC',
  shipped: '#72C7C0',
  purchasing: '#BA68C8',
  credits: '#F28E2C',
  creditsValue: '#FFB74D',
  trend: "#bde1ff",
  operationsCardBg: {
    creditQty: '#33333321',
    creditValue: '#33333321',
  },

} as const;

export type DashboardPalette = {
  orders: { store: string; online: string };
  orderValue: { store: string; online: string };
  salesMargins: { store: string; online: string };
  returns: string;
  toShip: string;
  toShipBackground: string;
  shipped: string;
  purchasing: string;
  credits: string;
  creditsValue: string;
  trend: string;
  operationsCardBg: { creditQty: string; creditValue: string };
};

/** Light theme: same hues, the light (Online) shades stepped darker so they still read on white. */
export const DASHBOARD_COLORS_LIGHT: DashboardPalette = {
  orders: { store: '#3d6f9c', online: '#7ab3e8' },
  orderValue: { store: '#2E7D32', online: '#7cc47f' },
  salesMargins: { store: '#12946f', online: '#6cc0a9' },
  returns: '#d64545',
  toShip: '#ec8b9c',
  toShipBackground: '#FDEDEC',
  shipped: '#3fa79e',
  purchasing: '#a64fb8',
  credits: '#e57f1c',
  creditsValue: '#f0a535',
  trend: '#3d6f9c',
  operationsCardBg: { creditQty: '#0000000a', creditValue: '#0000000a' },
};

const safeLuminance = (color: string): number | null => {
  try { return getLuminance(color); } catch { return null; }
};
const safeAlpha = (color: string, value: number, fallback: string) => {
  try { return alpha(color, value); } catch { return fallback; }
};

/**
 * True when the active MUI theme (DarkTheme / GreyGooseTheme from ThemeProviderWrapper) is dark.
 * Reads the real paper colour first, so it is right even if a theme doesn't set palette.mode.
 */
export const useIsDarkDashboard = (): boolean => {
  const theme = useTheme();
  const lum = safeLuminance(String(theme.palette.background.paper));
  return lum == null ? theme.palette.mode === 'dark' : lum < 0.35;
};

export const useDashboardColors = (): DashboardPalette => {
  const dark = useIsDarkDashboard();
  return dark ? DASHBOARD_COLORS : DASHBOARD_COLORS_LIGHT;
};

/** Colours/fonts the chart canvas needs (canvas can't read CSS), derived from the MUI theme. */
export type DashboardChartTheme = {
  dark: boolean;
  surface: string;
  text: string;
  textSecondary: string;
  grid: string;
  axis: string;
  border: string;
  fontFamily: string;
};

export const useDashboardChartTheme = (): DashboardChartTheme => {
  const theme = useTheme();
  const dark = useIsDarkDashboard();
  return useMemo(() => {
    const text = String(theme.palette.text.primary);
    return {
      dark,
      surface: String(theme.palette.background.paper),
      text,
      textSecondary: String(theme.palette.text.secondary),
      grid: safeAlpha(text, dark ? 0.08 : 0.09, dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'),
      axis: safeAlpha(text, 0.22, dark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.22)'),
      border: safeAlpha(text, 0.16, dark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.16)'),
      fontFamily: String(theme.typography.fontFamily ?? 'system-ui, sans-serif'),
    };
  }, [theme, dark]);
};
